import React, { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Mail, ChevronRight, ShieldCheck } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { makeAuthenticatedJsonRequest } from '../../utils/api'
import { trackEvent } from '../../utils/analytics'

/**
 * Conservative, corner-anchored (not full-screen) lead-capture card.
 * Arms only after a minimum dwell time, then fires on the first of two
 * signals: desktop exit-intent (cursor leaves via the top of the viewport)
 * or 50%+ scroll depth. One shot per arm — never re-triggers on the same
 * visit, and stays suppressed across visits for COOLDOWN_DAYS after a
 * dismiss (permanently after a submit).
 */

const DISMISS_KEY = 'zy:exit-capture-dismissed'
const SUBMITTED_KEY = 'zy:exit-capture-submitted'
const QUESTIONNAIRE_DONE_KEY = 'zy:questionnaire-done'
const COOLDOWN_DAYS = 14
const MIN_DWELL_MS = 15000
const SCROLL_TRIGGER_PCT = 50

const ExitIntentCapture: React.FC = () => {
  const { user, loading } = useAuth()
  const location = useLocation()
  const [armed, setArmed] = useState(false)
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const firedRef = useRef(false)

  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isAdminRoute = location.pathname.startsWith('/admin')
  const isIntentRoute = location.pathname.startsWith('/billing') || location.pathname.startsWith('/download')
  // Same gate the questionnaire uses — don't stack two lead surfaces on a
  // visitor who hasn't even cleared the first one yet.
  const questionnaireCleared = (() => {
    try {
      return Boolean(localStorage.getItem(QUESTIONNAIRE_DONE_KEY))
    } catch {
      return true
    }
  })()

  const eligible = !loading && !user && !isAdminRoute && !isIntentRoute && questionnaireCleared

  // Cooldown check on mount.
  useEffect(() => {
    try {
      if (localStorage.getItem(SUBMITTED_KEY)) {
        setDismissed(true)
        return
      }
      const dismissedAt = localStorage.getItem(DISMISS_KEY)
      if (dismissedAt) {
        const elapsedDays = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24)
        if (elapsedDays < COOLDOWN_DAYS) {
          setDismissed(true)
        }
      }
    } catch {
      /* private mode — treat as not dismissed */
    }
  }, [])

  // Arm after minimum dwell time.
  useEffect(() => {
    if (!eligible || dismissed) return
    const timer = window.setTimeout(() => setArmed(true), MIN_DWELL_MS)
    return () => window.clearTimeout(timer)
  }, [eligible, dismissed])

  const fire = (trigger: 'exit_intent' | 'scroll_depth') => {
    if (firedRef.current) return
    firedRef.current = true
    setVisible(true)
    trackEvent('exit_capture_shown', { trigger, path: location.pathname })
  }

  // Trigger 1: desktop exit intent — cursor leaves via the top edge.
  useEffect(() => {
    if (!armed || dismissed) return
    const onMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 0 && !e.relatedTarget) {
        fire('exit_intent')
      }
    }
    document.addEventListener('mouseout', onMouseOut)
    return () => document.removeEventListener('mouseout', onMouseOut)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, dismissed])

  // Trigger 2: 50%+ scroll depth (primary signal on mobile, where exit
  // intent via cursor position doesn't exist).
  useEffect(() => {
    if (!armed || dismissed) return
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      const pct = (window.scrollY / scrollable) * 100
      if (pct >= SCROLL_TRIGGER_PCT) {
        fire('scroll_depth')
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, dismissed])

  const close = () => {
    setVisible(false)
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      /* private mode — card will just re-arm next visit */
    }
    trackEvent('exit_capture_dismiss', { path: location.pathname })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmed = email.trim()
    if (!trimmed) {
      setError('Enter an email address.')
      return
    }
    setSubmitting(true)
    try {
      await makeAuthenticatedJsonRequest('/user/waitlist', {
        method: 'POST',
        body: {
          email: trimmed,
          source: 'exit_intent_popup',
          interestLevel: 'medium',
          metadata: { page: location.pathname },
        },
      })
      setSubmitted(true)
      trackEvent('exit_capture_submit_success', { path: location.pathname })
      try {
        localStorage.setItem(SUBMITTED_KEY, String(Date.now()))
      } catch {
        /* private mode */
      }
    } catch (err: any) {
      setError(err?.message || 'Could not subscribe. Try again in a moment.')
      trackEvent('exit_capture_submit_error', { path: location.pathname })
    } finally {
      setSubmitting(false)
    }
  }

  if (dismissed || !eligible) return null

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="exit-capture"
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.97, transition: { duration: 0.2 } }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          role="dialog"
          aria-label="Email signup"
          className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 w-auto sm:w-full sm:max-w-sm border border-white/10 bg-panel/80 rounded-xl shadow-2xl p-5"
        >
          <button
            onClick={close}
            aria-label="Dismiss"
            className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          {submitted ? (
            <div className="pr-4">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-mono-tech uppercase tracking-[0.22em] text-zypheron-400">
                <ShieldCheck className="h-3.5 w-3.5" />
                Confirmed
              </div>
              <p className="text-sm leading-6 text-gray-300">
                You're on the list. Next AD attack-path breakdown lands in your inbox.
              </p>
            </div>
          ) : (
            <div className="pr-4">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-mono-tech uppercase tracking-[0.22em] text-zypheron-400">
                <Mail className="h-3.5 w-3.5" />
                Before you go
              </div>
              <h3 className="text-base font-bold text-white font-display leading-snug">
                Get the AD attack-path breakdowns CISOs put in front of their boards
              </h3>
              <p className="mt-2 text-xs leading-5 text-gray-400">
                Real findings from live engagements, sent when there's something worth reading. No spam, unsubscribe anytime.
              </p>
              <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-label="Email address"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@company.com"
                  className="w-full border border-white/10 bg-[#07090a] px-3 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-gray-600 focus:border-zypheron-500 rounded-lg"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center justify-center gap-2 bg-zypheron-500 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-black transition hover:bg-zypheron-400 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg"
                >
                  <span>{submitting ? 'Sending' : 'Send it to me'}</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </form>
              {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ExitIntentCapture
